import {
  FormatBold as BoldIcon,
  FormatListBulleted as BulletListIcon,
  ExpandMore as ChevronIcon,
  Code as CodeIcon,
  LooksOne as Heading1Icon,
  LooksTwo as Heading2Icon,
  Looks3 as Heading3Icon,
  Highlight as HighlightIcon,
  FormatItalic as ItalicIcon,
  FormatListNumbered as OrderedListIcon,
  FormatStrikethrough as StrikethroughIcon,
  FormatUnderlined as UnderlineIcon,
} from "@mui/icons-material";
import { Menu, MenuItem } from "@mui/material";
import type { Editor } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import { type MouseEvent, useState } from "react";

import { Resources, useResource } from "Infrastructure/Translations/Resources";
import {
  DropdownTrigger,
  dropdownMenuProps,
  MenuToolbar,
  ToolbarButton,
  ToolbarDivider,
} from "Shared/TextEditor/editorStyles";

interface EditorBubbleMenuProps {
  editor: Editor;
}

export const EditorBubbleMenu = ({
  editor,
}: EditorBubbleMenuProps): React.ReactNode => {
  const { t } = useResource();
  const [headingAnchor, setHeadingAnchor] = useState<HTMLElement | null>(null);
  const [listAnchor, setListAnchor] = useState<HTMLElement | null>(null);

  const isAnyHeadingActive = editor.isActive("heading");
  const isAnyListActive =
    editor.isActive("bulletList") || editor.isActive("orderedList");

  const activeHeadingLabel = editor.isActive("heading", { level: 1 })
    ? t(Resources.TextEditor.Heading1)
    : editor.isActive("heading", { level: 2 })
      ? t(Resources.TextEditor.Heading2)
      : editor.isActive("heading", { level: 3 })
        ? t(Resources.TextEditor.Heading3)
        : t(Resources.TextEditor.Paragraph);

  const activeListIcon = editor.isActive("orderedList") ? (
    <OrderedListIcon />
  ) : (
    <BulletListIcon />
  );

  return (
    <BubbleMenu editor={editor}>
      <MenuToolbar>
        {/* Heading dropdown */}
        <DropdownTrigger
          type="button"
          $isActive={isAnyHeadingActive}
          onClick={(e: MouseEvent<HTMLButtonElement>) =>
            setHeadingAnchor(e.currentTarget)
          }
          title={activeHeadingLabel}
        >
          {editor.isActive("heading", { level: 1 }) ? (
            <Heading1Icon />
          ) : editor.isActive("heading", { level: 2 }) ? (
            <Heading2Icon />
          ) : editor.isActive("heading", { level: 3 }) ? (
            <Heading3Icon />
          ) : (
            <Heading1Icon />
          )}
          <ChevronIcon />
        </DropdownTrigger>
        <Menu
          anchorEl={headingAnchor}
          open={Boolean(headingAnchor)}
          onClose={() => setHeadingAnchor(null)}
          slotProps={dropdownMenuProps}
        >
          <MenuItem
            selected={!isAnyHeadingActive}
            onClick={() => {
              editor.chain().focus().setParagraph().run();
              setHeadingAnchor(null);
            }}
          >
            {t(Resources.TextEditor.Paragraph)}
          </MenuItem>
          <MenuItem
            selected={editor.isActive("heading", { level: 1 })}
            onClick={() => {
              editor.chain().focus().toggleHeading({ level: 1 }).run();
              setHeadingAnchor(null);
            }}
          >
            <Heading1Icon />
            {t(Resources.TextEditor.Heading1)}
          </MenuItem>
          <MenuItem
            selected={editor.isActive("heading", { level: 2 })}
            onClick={() => {
              editor.chain().focus().toggleHeading({ level: 2 }).run();
              setHeadingAnchor(null);
            }}
          >
            <Heading2Icon />
            {t(Resources.TextEditor.Heading2)}
          </MenuItem>
          <MenuItem
            selected={editor.isActive("heading", { level: 3 })}
            onClick={() => {
              editor.chain().focus().toggleHeading({ level: 3 }).run();
              setHeadingAnchor(null);
            }}
          >
            <Heading3Icon />
            {t(Resources.TextEditor.Heading3)}
          </MenuItem>
        </Menu>

        {/* List dropdown */}
        <DropdownTrigger
          type="button"
          $isActive={isAnyListActive}
          onClick={(e: MouseEvent<HTMLButtonElement>) =>
            setListAnchor(e.currentTarget)
          }
        >
          {activeListIcon}
          <ChevronIcon />
        </DropdownTrigger>
        <Menu
          anchorEl={listAnchor}
          open={Boolean(listAnchor)}
          onClose={() => setListAnchor(null)}
          slotProps={dropdownMenuProps}
        >
          <MenuItem
            selected={editor.isActive("bulletList")}
            onClick={() => {
              editor.chain().focus().toggleBulletList().run();
              setListAnchor(null);
            }}
          >
            <BulletListIcon />
            {t(Resources.TextEditor.BulletList)}
          </MenuItem>
          <MenuItem
            selected={editor.isActive("orderedList")}
            onClick={() => {
              editor.chain().focus().toggleOrderedList().run();
              setListAnchor(null);
            }}
          >
            <OrderedListIcon />
            {t(Resources.TextEditor.OrderedList)}
          </MenuItem>
        </Menu>

        <ToolbarDivider />

        {/* Inline formatting */}
        <ToolbarButton
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          $isActive={editor.isActive("bold")}
          title={t(Resources.TextEditor.Bold)}
        >
          <BoldIcon />
        </ToolbarButton>
        <ToolbarButton
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          $isActive={editor.isActive("italic")}
          title={t(Resources.TextEditor.Italic)}
        >
          <ItalicIcon />
        </ToolbarButton>
        <ToolbarButton
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          $isActive={editor.isActive("underline")}
          title={t(Resources.TextEditor.Underline)}
        >
          <UnderlineIcon />
        </ToolbarButton>
        <ToolbarButton
          type="button"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          $isActive={editor.isActive("strike")}
          title={t(Resources.TextEditor.Strikethrough)}
        >
          <StrikethroughIcon />
        </ToolbarButton>
        <ToolbarButton
          type="button"
          onClick={() => editor.chain().focus().toggleCode().run()}
          $isActive={editor.isActive("code")}
          title={t(Resources.TextEditor.Code)}
        >
          <CodeIcon />
        </ToolbarButton>
        <ToolbarButton
          type="button"
          onClick={() => editor.chain().focus().toggleHighlight().run()}
          $isActive={editor.isActive("highlight")}
          title={t(Resources.TextEditor.Highlight)}
        >
          <HighlightIcon />
        </ToolbarButton>
      </MenuToolbar>
    </BubbleMenu>
  );
};
